import React, { useRef, useState, useEffect } from 'react';
import { styled } from 'styletron-react';
import { S3Image } from './S3Image';
import { Upload, Image as ImageIcon, X as XIcon } from 'lucide-react';
import { getImageUrl } from '../utils/s3ImageUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const MAX_DIMENSION = 1920;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB after compression

interface S3ImageUploadProps {
  value: string;
  onChange: (key: string) => void;
  label?: string;
  previewHeight?: string;
  objectFit?: React.CSSProperties['objectFit'];
  /** 'default' = input + button + preview; 'slot' = clickable dashed dropzone. */
  variant?: 'default' | 'slot';
  /** Slot variant only — box dimensions and placeholder caption. */
  slotWidth?: string;
  slotHeight?: string;
  placeholder?: string;
  /** Called with the file the user picked, before it is compressed and uploaded (SAM-1105: look at its border). */
  onFileChosen?: (file: File) => void;
}

/**
 * Compress/resize image client-side before uploading.
 * Ensures images stay under API Gateway's payload limit.
 */
function isSvgValue(v: string): boolean {
  return v.toLowerCase().endsWith('.svg') || v.startsWith('data:image/svg');
}

function compressImage(file: File, maxDim = MAX_DIMENSION, quality = 0.85): Promise<File> {
  return new Promise((resolve, reject) => {
    // SVGs can't be drawn to canvas; skip compression entirely
    if (file.type === 'image/svg+xml') { resolve(file); return; }

    const isPng = file.type === 'image/png';

    // If small enough and doesn't need resizing, skip
    if (file.size <= MAX_FILE_SIZE) {
      resolve(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round(height * (maxDim / width));
          width = maxDim;
        } else {
          width = Math.round(width * (maxDim / height));
          height = maxDim;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(file); return; }

      // For JPEG: fill white background first (avoids black on transparent PNGs)
      if (!isPng) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Keep PNG format for transparent images, JPEG for photos
      const outputType = isPng ? 'image/png' : 'image/jpeg';
      const ext = isPng ? '.png' : '.jpg';

      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return; }
          resolve(new File([blob], file.name.replace(/\.\w+$/, ext), { type: outputType }));
        },
        outputType,
        isPng ? undefined : quality,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

// SAM-1309 (U16): the preview shows the image whole, in its own proportion, on a checkered background (a white or
// transparent logo is visible), at the height the caller asks for. Never stretched, never cropped, no text on failure.
const ImagePreview: React.FC<{ value: string; height: string }> = ({ value, height }) => {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let alive = true;
    getImageUrl(value).then((u) => { if (alive) setSrc(u); }).catch(() => { if (alive) setSrc(''); });
    return () => { alive = false; };
  }, [value]);
  return (
    <SvgPreviewWrap style={{ height, minWidth: height }}>
      {src && <img src={src} alt="Preview" style={{ height: '100%', maxWidth: '100%', objectFit: 'contain', display: 'block' }} onError={() => setSrc('')} />}
    </SvgPreviewWrap>
  );
};

export const S3ImageUpload: React.FC<S3ImageUploadProps> = ({
  value,
  onChange,
  label = 'Image',
  previewHeight = '200px',
  objectFit = 'cover',
  variant = 'default',
  slotWidth = '100%',
  slotHeight = '150px',
  placeholder,
  onFileChosen,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleUpload = async (file: File) => {
    onFileChosen?.(file);
    setUploading(true);
    setError(null);
    try {
      // Compress image to avoid 413 payload too large
      const compressed = await compressImage(file);

      const fd = new FormData();
      fd.append('file', compressed);
      const token = localStorage.getItem('token');
      let apiKey = import.meta.env.VITE_API_KEY;
      try {
        const bd = sessionStorage.getItem('businessData');
        if (bd) apiKey = JSON.parse(bd).api_key || apiKey;
      } catch {}
      const resp = await fetch(`${API_BASE_URL}/ocrservice/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'X-Api-Key': apiKey },
        body: fd,
      });
      if (resp.status === 413) {
        setError('Image is too large. Please use a smaller image.');
        return;
      }
      if (!resp.ok) throw new Error(`Upload failed: ${resp.status}`);
      const data = await resp.json();
      const imageKey = data.data?.picture || data.image_key || data.data?.image_key || data.data?.key;
      if (imageKey) {
        onChange(imageKey);
      } else {
        setError('Upload succeeded but could not get image key');
        console.error('[S3ImageUpload] response:', data);
      }
    } catch (err: any) {
      setError(err.message || 'Upload failed');
      console.error('[S3ImageUpload] error:', err);
    } finally {
      setUploading(false);
    }
  };

  if (variant === 'slot') {
    const caption = placeholder || label;
    return (
      <div>
        <SlotBox
          $hasImage={!!value}
          $over={dragOver}
          style={{ width: slotWidth, height: slotHeight }}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer.files?.[0];
            if (file) handleUpload(file);
          }}
        >
          {value ? (
            isSvgValue(value) ? (
              <ImagePreview value={value} height={slotHeight} />
            ) : (
              <S3Image
                imageKey={value}
                alt="Preview"
                style={{ width: '100%', height: '100%', objectFit }}
              />
            )
          ) : (
            <SlotEmpty>
              <ImageIcon size={26} />
              <span>{caption}</span>
            </SlotEmpty>
          )}
          {uploading && <SlotOverlay>Uploading…</SlotOverlay>}
          {value && !uploading && (
            <SlotRemove
              type="button"
              title="Remove image"
              onClick={(e) => { e.stopPropagation(); onChange(''); setError(null); }}
            >
              <XIcon size={13} />
            </SlotRemove>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*,image/svg+xml"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
              e.target.value = '';
            }}
          />
        </SlotBox>
        {error && <ErrorText>{error}</ErrorText>}
      </div>
    );
  }

  return (
    <div>
      {label && <Label>{label}</Label>}
      <input
        ref={fileRef}
        type="file"
        accept="image/*,image/svg+xml"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleUpload(file);
          e.target.value = '';
        }}
      />
      {value && <ImagePreview value={value} height={previewHeight} />}
      <UploadRow style={{ marginTop: value ? '8px' : 0 }}>
        <UploadButton
          type="button"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          <Upload size={14} />
          {uploading ? 'Uploading...' : value ? 'Replace' : 'Upload'}
        </UploadButton>
        {value && !uploading && (
          <RemoveButton type="button" onClick={() => { onChange(''); setError(null); }}>
            Remove
          </RemoveButton>
        )}
      </UploadRow>
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
};

const Label = styled('div', {
  fontSize: '13px',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '6px',
});

const UploadRow = styled('div', {
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
});

const RemoveButton = styled('button', {
  padding: '8px 12px',
  borderRadius: '8px',
  border: '1px solid #D1D5DB',
  backgroundColor: '#fff',
  color: '#374151',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap' as const,
  ':hover': { backgroundColor: '#F3F4F6' },
});

const UploadButton = styled('button', {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: 'var(--tenant-primary, #111827)',
  color: '#fff',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap' as const,
  ':hover': { backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' },
  ':disabled': { opacity: 0.5, cursor: 'not-allowed' },
});

const SvgPreviewWrap = styled('div', {
  borderRadius: '8px',
  border: '1px solid #D1D5DB',
  padding: '6px',
  boxSizing: 'border-box' as const,
  maxWidth: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#F9FAFB',
  backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)',
  backgroundSize: '12px 12px',
  backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0px',
});

const ErrorText = styled('div', {
  fontSize: '12px',
  color: '#EF4444',
  marginTop: '4px',
});

const SlotBox = styled('div', ({ $hasImage, $over }: { $hasImage?: boolean; $over?: boolean }) => ({
  position: 'relative',
  borderRadius: '14px',
  border: $hasImage ? '1px solid var(--line, #E5E7EB)' : '1.5px dashed var(--ink-300, #D1D5DB)',
  background: $over ? 'color-mix(in srgb, var(--tenant-primary, #111827) 6%, white)' : 'var(--surface, #F9FAFB)',
  borderColor: $over ? 'var(--tenant-primary, #111827)' : undefined,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  cursor: 'pointer',
  boxSizing: 'border-box' as const,
  transition: 'border-color 0.15s ease, background 0.15s ease',
  ':hover': { borderColor: 'var(--tenant-primary, #111827)' },
}));

const SlotEmpty = styled('div', {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  gap: '8px',
  color: 'var(--ink-400, #9CA3AF)',
  fontSize: '13px',
  fontWeight: 500,
  pointerEvents: 'none' as const,
});

const SlotOverlay = styled('div', {
  position: 'absolute',
  inset: '0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.7)',
  fontSize: '13px',
  fontWeight: 500,
  color: 'var(--ink-700, #374151)',
});

const SlotRemove = styled('button', {
  position: 'absolute',
  top: '8px',
  right: '8px',
  width: '24px',
  height: '24px',
  borderRadius: '999px',
  border: 'none',
  background: 'rgba(17,24,39,0.6)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  ':hover': { background: 'rgba(17,24,39,0.85)' },
});
